/**
 * RUM: web-vitals (LCP) + custom metrics.
 * console.table in dev; sendBeacon to VITE_BEACON_URL when set; else no-op.
 * No PII ever.
 */

import { onLCP, type LCPMetric } from 'web-vitals';

export interface ChapterLoadMetric {
  chapter: string;
  ms: number;
  ok: boolean;
}

export interface RumSnapshot {
  lcp: number | null;
  chapters: ChapterLoadMetric[];
  tier: string | null;
  seekP95Ms: number | null;
  ts: number;
}

const snapshot: RumSnapshot = {
  lcp: null,
  chapters: [],
  tier: null,
  seekP95Ms: null,
  ts: Date.now(),
};

let started = false;

export function getRumSnapshot(): Readonly<RumSnapshot> {
  return snapshot;
}

export function recordChapterLoad(
  chapter: string,
  ms: number,
  ok: boolean,
): void {
  snapshot.chapters.push({ chapter, ms, ok });
  snapshot.ts = Date.now();
  flush();
}

export function recordTier(tier: string): void {
  snapshot.tier = tier;
  snapshot.ts = Date.now();
  flush();
}

/** Seek p95 from scrubber samples (first load already excluded upstream). */
export function recordSeekP95(ms: number | null): void {
  snapshot.seekP95Ms = ms;
  snapshot.ts = Date.now();
  flush();
}

export function startRum(): void {
  if (started) return;
  started = true;

  onLCP((metric: LCPMetric) => {
    snapshot.lcp = metric.value;
    snapshot.ts = Date.now();
    flush();
  });
}

function isDev(): boolean {
  try {
    return import.meta.env.DEV === true;
  } catch {
    return false;
  }
}

function beaconUrl(): string | undefined {
  try {
    const url = import.meta.env.VITE_BEACON_URL;
    return typeof url === 'string' && url.length > 0 ? url : undefined;
  } catch {
    return undefined;
  }
}

/** Payload is aggregate metrics only — no PII, no URLs with query tokens. */
function buildPayload(): Record<string, unknown> {
  return {
    lcp: snapshot.lcp,
    chapters: snapshot.chapters.map((c) => ({
      chapter: c.chapter,
      ms: Math.round(c.ms),
      ok: c.ok,
    })),
    tier: snapshot.tier,
    seekP95Ms:
      snapshot.seekP95Ms === null ? null : Math.round(snapshot.seekP95Ms),
    ts: snapshot.ts,
  };
}

export function flush(): void {
  const payload = buildPayload();

  if (isDev()) {
    // eslint-disable-next-line no-console
    console.table({
      lcp: payload.lcp,
      tier: payload.tier,
      seekP95Ms: payload.seekP95Ms,
      chapterCount: snapshot.chapters.length,
      fails: snapshot.chapters.filter((c) => !c.ok).length,
    });
  }

  const url = beaconUrl();
  if (!url) return;

  try {
    const body = JSON.stringify(payload);
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
    }
  } catch {
    // no-op on beacon failure
  }
}

/** Test helper — reset module state. */
export function __resetRumForTests(): void {
  snapshot.lcp = null;
  snapshot.chapters.length = 0;
  snapshot.tier = null;
  snapshot.seekP95Ms = null;
  snapshot.ts = Date.now();
  started = false;
}
